import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { MenuItem, Select, Input, Chip, InputLabel } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useField } from '@unform/core';
import { find, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'querystring';

import { canURL } from '~/helpers';
import api from '~/services/api';

const useStyles = makeStyles((theme) => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    margin: 2,
  },
  noLabel: {
    marginTop: theme.spacing(3),
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function ComboboxMultiple({
  unique,
  label,
  url,
  params,
  title,
  name,
  first,
  hiddens,
  getValue,
  disabled,
  ...rest
}) {
  const classes = useStyles();
  const permission = isEqual(canURL(), { read: true });

  const [data, setData] = useState([]);
  const [personName, setPersonName] = React.useState([]);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);

  const {
    fieldName,
    defaultValue,
    registerField,
    error,
    clearError,
  } = useField(name);

  function handleChangeValue(event) {
    const { value } = event.target;

    setPersonName(value);
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef.current,
      path: 'value',
    });
  }, [registerField, fieldName]);

  useEffect(() => {
    async function getData() {
      try {
        await setLoading(true);
        const response = await api.get(url, {
          params: { ...params, pagesize: 999999, pesquisalista: true },
          paramsSerializer: (param) => qs.stringify(param),
        });

        await setData(response.data.items);
      } catch ({ erro }) {
        toast.error(erro);
      } finally {
        await setLoading(false);
      }
    }

    if (!isEmpty(url)) {
      getData();
    }
  }, [url, params]);

  useEffect(() => {
    getValue(personName);
    inputRef.current.value = personName;
  }, [personName]);

  useEffect(() => {
    if (!isEmpty(defaultValue) && !isEmpty(data)) {
      setPersonName(defaultValue);
    }

    if (!isEmpty(defaultValue) && isEmpty(url)) {
      setPersonName(defaultValue);
    }
  }, [defaultValue, data]);

  useEffect(() => {
    if (disabled) clearError();
  }, [disabled]);

  return (
    <>
      <InputLabel>{title}</InputLabel>
      <Select
        multiple
        ref={inputRef}
        onChange={handleChangeValue}
        error={!!error}
        value={personName}
        helperText={error}
        variant="outlined"
        fullWidth
        size="small"
        input={<Input id="select-multiple-chip" />}
        InputLabelProps={{
          shrink: true,
        }}
        renderValue={(selected) => (
          <div className={classes.chips}>
            {selected.map((value) => (
              <Chip
                key={value}
                label={
                  isEmpty(url)
                    ? value
                    : find(data, (o) => o[unique] === value)[label]
                }
                className={classes.chip}
              />
            ))}
          </div>
        )}
        MenuProps={MenuProps}
        {...rest}
        disabled={disabled || permission}
        onFocus={clearError}
      >
        {first.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}

        {data.map((option) => (
          <MenuItem key={option[unique]} value={option[unique]}>
            {option[label]}
          </MenuItem>
        ))}
      </Select>
    </>
  );
}

ComboboxMultiple.propTypes = {
  unique: PropTypes.string,
  label: PropTypes.string,
  url: PropTypes.string,
  params: PropTypes.objectOf(PropTypes.object),
  title: PropTypes.string,
  name: PropTypes.string.isRequired,
  first: PropTypes.arrayOf(PropTypes.object),
  hiddens: PropTypes.arrayOf(PropTypes.string),
  getValue: PropTypes.func,
  disabled: PropTypes.bool,
};

ComboboxMultiple.defaultProps = {
  unique: '',
  label: '',
  url: '',
  params: {},
  title: 'Select',
  first: [],
  hiddens: [],
  getValue: () => {},
  disabled: false,
};

export default ComboboxMultiple;
